import type {
  Business,
  BusinessForPublic,
  BusinessSettings,
  CreateBusinessRequest,
  UpdateBusinessRequest,
  BusinessEntitlements,
} from '@billx/types';

import { publicApiClient, userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';

export class BusinessService {
  /**
   * Create a new business
   */
  static async createBusiness(
    businessData: CreateBusinessRequest
  ): Promise<NucleusServiceResponse<Business>> {
    try {
      const response = await userApiClient.post<
        NucleusServiceResponse<Business>
      >('/businesses', businessData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create business');
    }
  }

  /**
   * Fetch a single business by ID
   */
  static async fetchBusinessById(
    businessId: string
  ): Promise<NucleusServiceResponse<Business>> {
    try {
      const response = await userApiClient.get(`/businesses/${businessId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch business');
    }
  }

  /**
   * Fetch a single business by ID for public
   */
  static async fetchBusinessByIdForPublic(
    businessId: string
  ): Promise<NucleusServiceResponse<BusinessForPublic>> {
    try {
      const response = await publicApiClient.get(
        `/public/businesses/${businessId}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch business');
    }
  }

  /**
   * Update a business
   */
  static async updateBusiness(
    businessId: string,
    updateData: UpdateBusinessRequest
  ): Promise<NucleusServiceResponse<Business>> {
    try {
      const response = await userApiClient.patch<
        NucleusServiceResponse<Business>
      >(`/businesses/${businessId}`, updateData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update business');
    }
  }

  /**
   * Update business settings
   */
  static async updateBusinessSettings<K extends keyof BusinessSettings>(
    businessId: string,
    key: K,
    value: BusinessSettings[K]
  ): Promise<NucleusServiceResponse<Business>> {
    try {
      const response = await userApiClient.patch<
        NucleusServiceResponse<Business>
      >(`/businesses/${businessId}/settings`, {
        [key]: value,
      });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update business settings');
    }
  }

  /**
   * Delete a business (soft delete)
   */
  static async deleteBusiness(
    businessId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.delete(`/businesses/${businessId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete business');
    }
  }

  static async getAccessibleBusinesses(): Promise<
    NucleusServiceResponse<Business[]>
  > {
    try {
      const response = await userApiClient.get('/businesses');

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch user businesses');
    }
  }

  /**
   * Fetch business entitlements
   */
  static async fetchBusinessEntitlements(
    businessId: string
  ): Promise<NucleusServiceResponse<BusinessEntitlements>> {
    try {
      const response = await userApiClient.get(
        `/businesses/${businessId}/entitlements`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch business entitlements');
    }
  }
}
